/**
 * useHiddenTokenRouting - the battlefield as the GM's screen sees it, hidden figures included (plan map-display, G1).
 *
 * `useBattlefieldSync` holds the SHARED tokens, one document each, which every
 * member reads. A hidden figure's token must never become one of them: it is
 * kept in the figure's `gmHidden` record (`useHiddenFigures`). This hook joins
 * the two for the map and routes each write by id:
 *
 *   - `tokens`: the shared tokens with the hidden ones laid over them (the same
 *     object when nothing is hidden).
 *   - `writeToken` / `removeToken`: a hidden id goes to its record, every
 *     other to the shared documents, unchanged.
 *   - A shared token with a hidden id - re-created by a late player write - is
 *     deleted at once (useSweepStrayTokens).
 *   - Terrain changes re-seat the hidden tokens the way `setTerrain` settles
 *     the shared ones (only on a new terrain, never on a drag).
 *
 * @param {object} battlefield - tokens, writeToken, removeToken, terrain (useBattlefieldSync)
 * @param {object} hidden - isHidden, hiddenTokens, placeToken (useHiddenFigures)
 * @returns {{ tokens, writeToken, removeToken }}
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import useMirror from './useMirror';
import { settleOnTerrain } from './useBattlefieldSync.js';

/**
 * Re-seat the hidden tokens when the terrain itself changes; the tokens are read through a ref. `settle` is the
 * shell's, which meets the Objects' boxes too (2026-10-26).
 */
function useReseatOnTerrain(terrain, hiddenTokens, placeToken, settle) {
  const latest = useMirror(hiddenTokens);
  const seen = useRef(terrain);
  useEffect(() => {
    if (seen.current === terrain) return;
    seen.current = terrain;
    const current = latest.current;
    if (!terrain || !Object.keys(current).length) return;
    const { tokens } = settle(current, terrain);
    for (const token of Object.values(tokens)) if (current[token.id] !== token) placeToken(token.id, token);
  }, [terrain, placeToken, latest, settle]);
}

/**
 * A shared token carrying a hidden id is deleted the moment it appears. The
 * hide deleted it, but a player's late write (a drag their screen still held)
 * re-creates it, and no server rule can know who is hidden: the GM's screen
 * sweeps it, so it lives for a round trip at most (fuzz 2026-10-07, seed 800597).
 */
function useSweepStrayTokens(shared, isHidden, removeShared) {
  useEffect(() => {
    for (const id of Object.keys(shared ?? {})) if (isHidden(id)) removeShared(id);
  }, [shared, isHidden, removeShared]);
}

export default function useHiddenTokenRouting(battlefield, hidden, settle = settleOnTerrain) {
  const { tokens: shared, writeToken: writeShared, removeToken: removeShared, terrain } = battlefield;
  // `isHidden`, not the rendered set: a scene cut restores hidden figures and writes their tokens in one tick.
  const { isHidden, hiddenTokens, placeToken } = hidden;
  useReseatOnTerrain(terrain, hiddenTokens, placeToken, settle);
  useSweepStrayTokens(shared, isHidden, removeShared);
  const tokens = useMemo(
    () => (Object.keys(hiddenTokens).length ? { ...shared, ...hiddenTokens } : shared),
    [shared, hiddenTokens],
  );
  const writeToken = useCallback(
    (token, options) => (isHidden(token.id) ? placeToken(token.id, token) : writeShared(token, options)),
    [isHidden, placeToken, writeShared],
  );
  const removeToken = useCallback(
    (id) => (isHidden(id) ? placeToken(id, null) : removeShared(id)),
    [isHidden, placeToken, removeShared],
  );
  return { tokens, writeToken, removeToken };
}
